import findIndex from 'lodash/findIndex'
import isNil from 'lodash/isNil'
import uniqueId from 'lodash/uniqueId'

import { Icon } from 'Shared/atoms'

import { stringToLabelValue } from 'Core/utils'

import { Kpi, MeasureType, TermCodeType } from 'Portals/Customer/entities'
import {
	getLastMonthNamesWithYearForProvidedMonthCount,
	getLastQuaterNamesForProvidedQuaterCount,
	getLastYearsForProvidedMonthCount
} from 'Portals/Customer/utils'

import { KpiCellType } from '../KpiTable/CellWrapper/utils/constants'

import styles from '../styles.module.scss'
export const getKpiValueOptions = (entity: Entity[]) =>
	entity.map((val) => ({
		label: val.name,
		value: val.name
	}))
export const getCorrectMeasureTypeColumn = (measureType: MeasureType) => {
	switch (measureType) {
		case MeasureType.number:
			return KpiCellType.input
		case MeasureType.string:
			return KpiCellType.input
		case MeasureType.boolean:
			return KpiCellType.select
		case MeasureType.predefinedString:
			return KpiCellType.select
	}
}

export const getYearPeriod = (
	termCodeType: TermCodeType,
	quantity?: number
): ExpandedLabelValue[] => {
	switch (termCodeType) {
		case TermCodeType.YEARLY:
			return getLastYearsForProvidedMonthCount(quantity)
		case TermCodeType.MONTHLY:
			return getLastMonthNamesWithYearForProvidedMonthCount(quantity)
		case TermCodeType.QUARTERLY:
			return getLastQuaterNamesForProvidedQuaterCount(quantity)
	}
}

export const getCorrectUOM = (item: CodeEntity) => ({
	value: item.code,
	label: item.name,
	key: uniqueId()
})

export const prepareRequest = (
	formData: Kpi.Form.KpiList,
	serviceSiteId: number
): Kpi.Request.Add => {
	const type = getCorrectMeasureTypeColumn(formData.measureType)
	const quantity =
		type === KpiCellType.select
			? (formData.quantity as LabelValue)?.label
			: (formData.quantity as string)

	const measureTermCode =
		formData.termCodeType === TermCodeType.YEARLY ? 'YEAR' : formData.year?.saveValue

	return {
		kpiId: formData.id,
		rowKey: formData.rowKey,
		serviceSiteId: serviceSiteId,
		year: formData.year?.extra,
		measureTermCode,
		measureValue: quantity,
		uomCode: formData.uom?.value
	}
}

export const prepareEditRequest = (formData: Kpi.Form.KpiList): Kpi.Request.Edit => {
	const type = getCorrectMeasureTypeColumn(formData.measureType)
	const quantity =
		type === KpiCellType.select
			? (formData.quantity as LabelValue)?.label
			: (formData.quantity as string)
	return {
		measureValue: quantity,
		uomCode: formData.uom?.value
	}
}

export const resetFormWithMeasure = (
	measures: Kpi.KpiLocationMeasureForm[],
	fields: Kpi.Form.Edit,
	selectedKpiId: number
): { field: Kpi.Form.KpiList; index: number } | undefined => {
	const index = findIndex(fields.list, (kpi) => kpi.rowKey === selectedKpiId)
	if (index === -1) {
		return
	}
	const field = fields.list[index]

	let newField: Kpi.Form.KpiList = {
		...field,
		uom: !!field.kpiUOMs.length ? getCorrectUOM(field.kpiUOMs[0]) : null,
		quantity: null
	}
	const measure = measures.find((measure) => measure.rowKey === selectedKpiId)
	if (measure) {
		const uom = field.kpiUOMs.find((uom) => uom.code === measure.uomCode)
		newField.uom = uom ? getCorrectUOM(uom) : null
		newField.quantity =
			field.measureType === MeasureType.number || field.measureType === MeasureType.string
				? measure.value
				: stringToLabelValue(measure.value)
	}
	return {
		field: newField,
		index
	}
}

export const getDefaultValues = (record: Kpi.Basic[]): Kpi.Form.Edit => ({
	list: record.map((item) => ({
		...item,
		year: item.year || null,
		quantity: item.quantity || null,
		uom: item.uom ? item.uom : !!item.kpiUOMs.length ? getCorrectUOM(item.kpiUOMs[0]) : null
	}))
})

export const getDirtyData = ({
	dirtyConfig,
	measure,
	savedMeasureData,
	rowKey
}: {
	dirtyConfig: Kpi.DirtyConfig[]
	measure: Kpi.KpiLocationMeasureForm[]
	savedMeasureData: Kpi.SavedMeasureData[]
	rowKey: number
}) => {
	const dirtyField = dirtyConfig.find((conf) => conf.rowKey === rowKey)
	const correctMeasure = measure.find((measure) => measure.rowKey === rowKey)
	const isUpdate = !!correctMeasure || !!savedMeasureData.find((val) => val.rowKey === rowKey)
	const btnText = isUpdate ? 'Update' : 'Save'
	return {
		isDirty: dirtyField?.isDirty,
		btnText,
		isUpdate
	}
}

export const getKpiRow = (
	field: Kpi.FieldType | Kpi.Basic,
	successfullySavedId: Key | null,
	dailyCount: number | null,
	isMetric?: boolean
) => {
	const row = isMetric ? (
		<div className={styles.kpiMetricCol}>
			{field.companyserviceSiteId} - {field.name}
		</div>
	) : (
		<div className={styles.kpiNameColumn}>
			<span>{field.name}</span>
			<span>{field.description}</span>
		</div>
	)
	if (successfullySavedId === field.rowKey && !isNil(dailyCount)) {
		return (
			<div className={styles.successfullySaved}>
				{row}
				<div className={styles.block}>
					<Icon type="circleSuccessFilled" /> Successfully saved! ({dailyCount})
				</div>
			</div>
		)
	}
	return row
}
